import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, X, Pencil, Trash2, Loader2, Search, FolderKanban, ChevronRight, ChevronDown, Eye } from "lucide-react";
import { useProjectStore } from "../../store/useProjectStore";
import { useClientStore } from "../../store/useClientStore";
import ProjectForm from "../../components/projects/ProjectForm";
import EditProjectModal from "../../components/projects/EditProjectModal";
import ProjectViewModal from "../../components/projects/ProjectViewModal";
import MilestonePaymentPicker from "../../components/projects/MilestonePaymentPicker";

const money = (n: any) =>
  n === null || n === undefined || n === "" ? "—" : `$${Number(n).toLocaleString()}`;

const totalPrice = (p: any) => {
  if (p.projectPrice && Number(p.projectPrice) > 0) return Number(p.projectPrice);
  const sum = (Number(p.escrowAmount) || 0) + (Number(p.releasedPayment) || 0);
  return sum > 0 ? sum : null;
};

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const msColor = (s?: string) =>
  s === "Released" ? "text-[#0f8a63]" : s === "In Escrow" ? "text-amber-600" : "text-on-surface-variant";

// Distinct chip when the project came from an Invitation or a Direct client;
// Upwork/bid-generated projects stay unmarked to keep the table clean.
const sourceChip = (p: any) =>
  p.source === "Invitation"
    ? { label: "INVITED", cls: "bg-violet-100 text-violet-700" }
    : p.source === "Direct"
      ? { label: "DIRECT", cls: "bg-amber-100 text-amber-700" }
      : null;

// A requirement rejected by the Project Manager blocks the project — the row
// turns red and the reason is one hover away.
const hasRejection = (p: any) => (p.requirementChecklist || []).some((c: any) => c.status === "Rejected");
const rejectionReason = (p: any) =>
  (p.requirementChecklist || [])
    .filter((c: any) => c.status === "Rejected")
    .map((c: any) => `${c.label}: ${c.rejectionNote || "no reason given"}`)
    .join(" · ");

const statusPill = (s?: string) => {
  const base = "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap";
  switch (s) {
    case "Completed": return `${base} bg-[#10b981]/15 text-[#0f8a63]`;
    case "In Progress": return `${base} bg-primary/15 text-primary`;
    case "Cancelled": return `${base} bg-error-container text-error`;
    case "On Hold": return `${base} bg-amber-100 text-amber-700`;
    default: return `${base} bg-surface-container text-on-surface-variant`;
  }
};

export default function BDProjectsView() {
  const { projects, fetchProjects, deleteProject, isFetchingProjects, isDeletingProject } = useProjectStore();
  const { fetchClients } = useClientStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  // Resuming a saved Draft opens the multi-step creation modal pre-filled.
  const [resumingDraft, setResumingDraft] = useState<any>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [initialBidId, setInitialBidId] = useState<string | undefined>();
  const toggle = (id: string) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  // The wizard lends us its guard so a backdrop click cannot throw away four
  // steps of entered project detail. Null until the form mounts.
  const wizardGuard = useRef<((proceed: () => void) => void) | null>(null);
  const requestCloseDrawer = () => {
    if (wizardGuard.current) wizardGuard.current(closeDrawer);
    else closeDrawer();
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setInitialBidId(undefined);
    setResumingDraft(null);
    fetchProjects(true); // a Save-as-Draft may have happened inside the modal
  };
  // Drafts open in the creation wizard (resume); active projects use the editor.
  const openEditor = (p: any) => {
    if (p.isDraft) {
      setResumingDraft(p);
      setDrawerOpen(true);
    } else {
      setEditing(p);
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Opened via "Create project from won bid" on the Bids page.
  useEffect(() => {
    const bidId = (location.state as any)?.createFromBid;
    if (bidId) {
      setInitialBidId(bidId);
      setDrawerOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  const filtered = useMemo(
    () => projects.filter((p: any) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase())),
    [projects, q]
  );

  const handleDelete = async (id: string) => {
    if (window.confirm("Delete this project?")) await deleteProject(id);
  };

  return (
    <div className="bd-workspace p-6 lg:p-8 flex flex-col gap-6 max-w-[1400px] mx-auto w-full">
      {/* Header */}
      <section className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-[26px] leading-tight font-bold text-on-surface mb-1 flex items-center gap-2">
            <FolderKanban size={24} className="text-primary" /> Projects
          </h2>
          <p className="text-[14px] text-on-surface-variant">Track your projects, pricing, and delivery status.</p>
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#004b9e] transition-colors shrink-0"
        >
          <Plus size={18} /> Add Project
        </button>
      </section>

      {/* Table card */}
      <section className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-4 lg:p-5">
        <div className="relative max-w-sm mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search project name…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-surface-container-low border border-outline-variant text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {isFetchingProjects ? (
          <div className="flex items-center justify-center gap-2 py-16 text-on-surface-variant">
            <Loader2 size={18} className="animate-spin text-primary" /> Loading projects…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-outline">No projects yet. Click “Add Project” to create one.</div>
        ) : (
          <div className="overflow-x-auto cre-scroll">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-outline-variant">
                  {["Project", "Type", "Status", "Created", "Total Price", "Released", "Payment", "ETA", "Milestones", ""].map((h) => (
                    <th key={h} className="py-2.5 px-3 text-[11px] font-bold text-outline uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-[13px] text-on-surface">
                {filtered.map((p: any) => {
                  const ms = p.milestones || [];
                  const releasedCount = ms.filter((m: any) => m.status === "Released").length;
                  const isOpen = !!open[p._id];
                  return (
                  <Fragment key={p._id}>
                  <tr className={`border-b transition-colors ${hasRejection(p) ? "border-red-300 bg-red-50 hover:bg-red-100/70" : "border-outline-variant hover:bg-surface-container-low/60"}`}>
                    <td className="py-3 px-3 font-semibold text-on-surface">
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        {p.name}
                        {hasRejection(p) && (
                          <span className="px-1.5 py-px rounded-full text-[9px] font-bold tracking-wide bg-red-600 text-white" title={rejectionReason(p)}>
                            REJECTED
                          </span>
                        )}
                        {sourceChip(p) && (
                          <span className={`px-1.5 py-px rounded-full text-[9px] font-bold tracking-wide ${sourceChip(p)!.cls}`}>
                            {sourceChip(p)!.label}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-on-surface-variant whitespace-nowrap">{p.projectType || "—"}</td>
                    <td className="py-3 px-3">
                      {p.isDraft ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap bg-amber-100 text-amber-700 border border-amber-200" title="Saved draft — open it to continue project creation">
                          DRAFT
                        </span>
                      ) : (
                        <span className={statusPill(p.projectStatus)}>{p.projectStatus || "—"}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-on-surface-variant whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                    <td className="py-3 px-3 font-bold text-on-surface whitespace-nowrap">{money(totalPrice(p))}</td>
                    <td className="py-3 px-3 text-on-surface whitespace-nowrap">{p.releasedPayment ? money(p.releasedPayment) : "—"}</td>
                    <td className="py-3 px-3 text-on-surface-variant whitespace-nowrap">{p.paymentStatus || "—"}</td>
                    <td className="py-3 px-3 text-on-surface-variant whitespace-nowrap">{p.eta ? fmtDate(p.eta) : "—"}</td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {ms.length === 0 ? (
                        <span className="text-outline">—</span>
                      ) : (
                        <button onClick={() => toggle(p._id)} className="inline-flex items-center gap-1 text-primary font-semibold hover:underline">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          {ms.length}{releasedCount > 0 ? ` · ${releasedCount} rel.` : ""}
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setViewing(p)} title="View"
                          className="p-1.5 rounded-lg bg-surface-container hover:bg-surface-container-high text-on-surface-variant transition-colors">
                          <Eye size={15} />
                        </button>
                        <button onClick={() => openEditor(p)} title={p.isDraft ? "Resume draft" : "Edit"}
                          className="p-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleDelete(p._id)} disabled={isDeletingProject} title="Delete"
                          className="p-1.5 rounded-lg bg-error-container hover:bg-error-container/70 text-error transition-colors disabled:opacity-50">
                          {isDeletingProject ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && ms.length > 0 && (
                    <tr className="bg-surface-container-low/40 border-b border-outline-variant">
                      <td colSpan={10} className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {ms.map((m: any, i: number) => (
                            <div key={i} className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-[12px] min-w-[150px]">
                              <div className="font-semibold text-on-surface truncate">{m.title || `Milestone ${i + 1}`}</div>
                              <div className="text-on-surface-variant mt-1 flex flex-wrap items-center gap-1">
                                <span className="font-semibold text-on-surface">{money(m.amount)}</span>
                                {/* Work status — owned by the delivery team in ERP */}
                                <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-blue-50 text-blue-700" title="Work status — from the delivery team">
                                  {m.lifecycleStatus || "Backlog / Not Started"}
                                </span>
                                {/* Payment status — owned by this milestone's
                                    invoice, and now editable from here. The
                                    picker writes the invoice, not the badge. */}
                                <MilestonePaymentPicker
                                  paymentState={m.paymentState || "Pending"}
                                  invoice={m.invoice || null}
                                  onChanged={() => fetchProjects(true)}
                                />
                                {m.dueDate ? <span>· {fmtDate(m.dueDate)}</span> : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Project Creation — multi-step MODAL (centered, responsive) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={requestCloseDrawer} />
          <div className="relative w-full max-w-5xl max-h-[92vh] bg-white rounded-2xl shadow-2xl border border-slate-200/80 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200 shrink-0">
              <div>
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                  {resumingDraft ? "Resume Draft Project" : initialBidId ? "New Project from Won Bid" : "Create New Project"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Configure client details, project timeline, and operational delivery parameters.</p>
              </div>
              <button onClick={requestCloseDrawer} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            {/* The form owns the stepper band, the scrolling body and the footer band */}
            <ProjectForm initialBidId={initialBidId} initialProject={resumingDraft} onSuccess={closeDrawer} registerCloseGuard={(g) => { wizardGuard.current = g; }} />
          </div>
        </div>
      )}

      {editing && <EditProjectModal project={editing} onClose={() => setEditing(null)} />}
      {viewing && (
        <ProjectViewModal
          project={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => { const p = viewing; setViewing(null); setEditing(p); }}
        />
      )}
    </div>
  );
}
